// src/hooks/useUserProfile.js
import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { db } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { getCurrentUser, getUserProfile } from "../services/userService";
import friendshipService from "../services/friendshipService";

// Union of statuses returned by server + derived locally
// 'self' | 'friends' | 'requested' | 'pending' | 'blocked' | 'blocked_by' | 'none'
const deriveStatusFromLocalDoc = (snap) => {
  if (!snap.exists()) return "none";
  const d = snap.data();
  if (d.status === "accepted") return "friends";
  if (d.status === "blocked") return "blocked"; // current user blocked target
  if (d.status === "pending") return "pending"; // target initiated -> we see pending
  return "none";
};

/**
 * Hook for viewing & managing another user's profile (subcollection-based friends model)
 * - Single server-side check for cross-user state ('requested', 'blocked_by')
 * - Realtime listener to our own friend doc for reactive transitions
 */
const useUserProfile = (userId) => {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [isCurrentUser, setIsCurrentUser] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const [friendStatus, setFriendStatus] = useState("none");
  const statusRef = useRef(friendStatus);
  useEffect(() => { statusRef.current = friendStatus; }, [friendStatus]);

  // Fetch profile + initial relationship status
  useEffect(() => {
    let unsub = null;
    let alive = true;

    const run = async () => {
      setLoadingProfile(true);
      setProfileError(null);
      setFriendStatus("none");

      const currentUser = getCurrentUser();
      if (!currentUser) {
        if (alive) {
          setProfileError(t("errors.user_not_authenticated"));
          setLoadingProfile(false);
        }
        return;
      }

      const own = userId === currentUser.uid;
      if (alive) setIsCurrentUser(own);

      try {
        const userProfile = await getUserProfile(userId);
        if (alive) setProfile(userProfile);
      } catch (e) {
        console.error("Error fetching profile:", e);
        if (alive) {
          setProfile(null);
          setProfileError(
            e?.message === "User not found"
              ? t("profile.not_found")
              : t("profile.fetch_error")
          );
        }
        setLoadingProfile(false);
        return;
      }

      // If viewing self, no friendship status needed
      if (own) {
        if (alive) {
          setFriendStatus("self");
          setLoadingProfile(false);
        }
        return;
      }

      // 1) Initial cross-user status (server-side, per ADR security)
      try {
        const status = await friendshipService.checkFriendshipStatus(userId);
        if (alive) setFriendStatus(status);
      } catch (e) {
        console.warn("checkFriendshipStatus failed, defaulting to none", e);
      }

      // 2) Realtime subscription to *our* friend doc for this target
      const ref = doc(db, "users", currentUser.uid, "friends", userId);
      unsub = onSnapshot(
        ref,
        async (snap) => {
          const local = deriveStatusFromLocalDoc(snap);

          // The local listener can't see:
          // - 'requested' (we initiated; doc lives in target's subcol)
          // - 'blocked_by' (the reverse doc)
          // So: if local yields 'none', fall back to server status once.
          if (local === "none") {
            // Keep 'requested' or 'blocked_by' sticky until state changes
            if (statusRef.current === "requested" || statusRef.current === "blocked_by") {
              // No change; wait for acceptance or cancellation to reflect
              return;
            }
            // Re-check server to confirm it's truly 'none'
            try {
              const s = await friendshipService.checkFriendshipStatus(userId);
              setFriendStatus(s);
            } catch {
              setFriendStatus("none");
            }
            return;
          }

          setFriendStatus(local);
        },
        (err) => {
          console.error("Friend doc subscription error", err);
          // Non-fatal: keep last known status
        }
      );

      if (alive) setLoadingProfile(false);
    };

    if (userId) run();

    return () => {
      alive = false;
      unsub?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, t]);

  // --- Actions (thin wrappers over friendship service) ---

  const sendFriendRequest = useCallback(async () => {
    try {
      await friendshipService.sendFriendRequest(userId);
      // Optimistic: remote doc lives in recipient; our listener will flip to 'friends' when accepted
      setFriendStatus("requested");
      return true;
    } catch (e) {
      console.error("Error sending friend request:", e);
      return false;
    }
  }, [userId]);

  const cancelFriendRequest = useCallback(async () => {
    try {
      await friendshipService.cancelFriendRequest(userId);
      setFriendStatus("none");
      return true;
    } catch (e) {
      console.error("Error cancelling friend request:", e);
      return false;
    }
  }, [userId]);

  const acceptRequest = useCallback(async () => {
    try {
      await friendshipService.acceptFriendRequest(userId);
      setFriendStatus("friends");
      return true;
    } catch (e) {
      console.error("Error accepting friend request:", e);
      return false;
    }
  }, [userId]);

  const declineRequest = useCallback(async () => {
    try {
      await friendshipService.declineFriendRequest(userId);
      setFriendStatus("none");
      return true;
    } catch (e) {
      console.error("Error declining friend request:", e);
      return false;
    }
  }, [userId]);

  const removeFriendship = useCallback(async () => {
    try {
      await friendshipService.removeFriend(userId);
      setFriendStatus("none");
      return true;
    } catch (e) {
      console.error("Error removing friend:", e);
      return false;
    }
  }, [userId]);

  // Optional: block/unblock if/when you add UI
  // const blockUser = useCallback(async () => { ... }, [userId]);
  // const unblockUser = useCallback(async () => { ... }, [userId]);

  return {
    profile,
    isCurrentUser,
    loadingProfile,
    profileError,
    setProfile,

    friendStatus,
    // actions
    sendFriendRequest,
    cancelFriendRequest,
    acceptRequest,
    declineRequest,
    removeFriendship,
  };
};

export default useUserProfile;